import React, { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useContract } from "../../hooks/useContract";
import { useUserData } from "../../hooks/useUserData";
import ClaimSection from "../../components/ClaimSection/ClaimSection";
import Loader from "../../components/Loader/Loader";
import { formatToken, truncateAddress, copyToClipboard, formatDate } from "../../utils/format";
import { TOKEN_SYMBOL, NETWORK, PLAN1_PKG_COUNT, PLAN2_PKG_COUNT } from "../../config/config";
import { PLAN1_PKG_LABELS, PLAN2_PKG_LABELS } from "../../utils/constants";
import { toast } from "react-toastify";
import "./HomeTab.css";

export default function HomeTab() {
  const { account } = useWeb3();
  const contracts = useContract();
  const { userId, userInfo, loading, fetchUserData } = useUserData();
  const [searchParams] = useSearchParams();
  const [p1Income, setP1Income] = useState(null);
  const [p2Income, setP2Income] = useState(null);
  const [pendingP2Global, setPendingP2Global] = useState(null);
  const [claimableP2Global, setClaimableP2Global] = useState(null);
  const [autoUpgradeHold, setAutoUpgradeHold] = useState(null);
  const [pendingP1Royalty, setPendingP1Royalty] = useState(null);
  const [pendingP2Royalty, setPendingP2Royalty] = useState(null);
  const [p1RoyaltyAchiever, setP1RoyaltyAchiever] = useState(false);
  const [p2RoyaltyAchiever, setP2RoyaltyAchiever] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [p1Prices, setP1Prices] = useState([]);
  const [p2Prices, setP2Prices] = useState([]);
  const [starTeamCount, setStarTeamCount] = useState(0);
  const [globalTeamCount, setGlobalTeamCount] = useState(0);
  const [starHighestPkg, setStarHighestPkg] = useState(-1);
  const [activeInvested, setActiveInvested] = useState(null);
  const [globalHighestPkg, setGlobalHighestPkg] = useState(-1);
  const [joinDate, setJoinDate] = useState(null);
  const [inactiveMembers, setInactiveMembers] = useState([]);
  const [inactivePage, setInactivePage] = useState(0);
  const [directMembers, setDirectMembers] = useState([]);
  const [directPage, setDirectPage] = useState(0);
  const [showDirect, setShowDirect] = useState(false);
  const [directLoading, setDirectLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [inactiveLoading, setInactiveLoading] = useState(false);

  // Register & Upgrade
  const [regWallet, setRegWallet] = useState("");
  const [regName, setRegName] = useState("");
  const [regRef, setRegRef] = useState("");
  const [regRefName, setRegRefName] = useState("");
  const [upgradeId, setUpgradeId] = useState("");
  const [upgradeRefName, setUpgradeRefName] = useState("");
  const [upgradePlan, setUpgradePlan] = useState("1");
  const [upgradePkg, setUpgradePkg] = useState("0");
  const [actionLoading, setActionLoading] = useState(false);

  const ref = searchParams.get("ref");

  useEffect(() => {
    if (account && contracts && !fetched) { loadAll(); setFetched(true); }
    // eslint-disable-next-line
  }, [account, contracts]);

  async function loadAll() {
    const info = await fetchUserData();
    if (!info) return;
    try {
      const [inc1, inc2, p2PendGlobal, p1PendRoy, p2PendRoy, prices1, prices2, p1LvlCounts, p1Pkgs, p2Pkgs] = await Promise.all([
        contracts.plan1.getIncomeStats(info.id), contracts.plan2.getIncomeStats(info.id),
        contracts.plan2.getAllPendingGlobal(info.id),
        contracts.plan1.getPendingRoyalty(info.id), contracts.plan2.getPendingRoyalty(info.id),
        contracts.plan1.getPackagePrices(), contracts.plan2.getPackagePrices(),
        contracts.plan1.getMatrixLevelCounts(info.id),
        contracts.plan1.getAllPackages(info.id), contracts.plan2.getAllPackages(info.id),
      ]);
      setP1Income({ direct: inc1.direct, level: inc1.lvl, royalty: inc1.royalty });
      setP2Income({ direct: inc2.direct, level: inc2.lvl, global: inc2.glob, royalty: inc2.royalty });
      setP1Prices(prices1); setP2Prices(prices2);
      const starTotal = p1LvlCounts.reduce((s, c) => s + c.toNumber(), 0);
      setStarTeamCount(starTotal);
      // Global team count - use pkg 0 as representative
      try {
        const p2LvlCounts = await contracts.plan2.getMatrixLevelCounts(info.id, 0);
        setGlobalTeamCount(p2LvlCounts.reduce((s, c) => s + c.toNumber(), 0));
      } catch { setGlobalTeamCount(0); }
      // Find highest active package
      let sh = -1; for (let i = 7; i >= 0; i--) { if (p1Pkgs.a[i]) { sh = i; break; } } setStarHighestPkg(sh);
      let gh = -1; for (let i = 7; i >= 0; i--) { if (p2Pkgs.a[i]) { gh = i; break; } } setGlobalHighestPkg(gh);

      // Compute Invested = sum of active package prices (current stake level)
      let inv = ethers.BigNumber.from(0);
      for (let i = 0; i < 8; i++) {
        if (p1Pkgs.a[i] && prices1[i]) inv = inv.add(prices1[i]);
        if (p2Pkgs.a[i] && prices2[i]) inv = inv.add(prices2[i]);
      }
      setActiveInvested(inv);
      // Get join date from oldest activity
      try {
        const acts = await contracts.data.getActivity(info.id, 0, 1);
        const total = acts.total.toNumber();
        if (total > 0) {
          const lastPage = Math.floor((total - 1) / 1);
          const oldest = await contracts.data.getActivity(info.id, lastPage, 1);
          if (oldest.acts.length > 0) setJoinDate(oldest.acts[0].timestamp);
        }
      } catch {}
      // Sum all pending global shares (for display)
      let p2d = p2PendGlobal[0]; for (let i = 1; i < 8; i++) p2d = p2d.add(p2PendGlobal[i]); setPendingP2Global(p2d);

      // Claimable portion: exclude highest active package's share if it's NOT the last package
      // (because that share goes to auto-upgrade hold balance, not to wallet)
      let p2cd = ethers.BigNumber.from(0);
      for (let i = 0; i < 8; i++) {
        if (gh < 7 && i === gh) continue;
        p2cd = p2cd.add(p2PendGlobal[i]);
      }
      setClaimableP2Global(p2cd);

      // Auto Upgrade Hold = current holdBalance + pending share of highest pkg (that will go to hold)
      // Exclude pkg index 7 (last package, $128) - it never auto-upgrades
      let hold = ethers.BigNumber.from(0);
      if (p1Pkgs.h) for (let i = 0; i < 7; i++) hold = hold.add(p1Pkgs.h[i]);
      if (p2Pkgs.h) for (let i = 0; i < 7; i++) hold = hold.add(p2Pkgs.h[i]);
      // Add pending Plan2 share of highest active pkg (only if not last - will go to hold)
      if (gh >= 0 && gh < 7) hold = hold.add(p2PendGlobal[gh]);
      setAutoUpgradeHold(hold);

      setPendingP1Royalty(p1PendRoy); setPendingP2Royalty(p2PendRoy);

      // Royalty achiever status (all 8 packages active)
      try {
        const [e1, e2] = await Promise.all([
          contracts.plan1.royaltyEligible(info.id),
          contracts.plan2.royaltyEligible(info.id),
        ]);
        setP1RoyaltyAchiever(e1);
        setP2RoyaltyAchiever(e2);
      } catch {}
    } catch (err) { console.error("HomeTab load:", err); }
  }

  function getTotalAvailable() {
    let t = userInfo?.claimable;
    if (!t) return null;
    // Use claimable portions (excluding shares that will go to auto-upgrade hold)
    if (claimableP2Global && !claimableP2Global.isZero()) t = t.add(claimableP2Global);
    if (pendingP1Royalty && !pendingP1Royalty.isZero()) t = t.add(pendingP1Royalty);
    if (pendingP2Royalty && !pendingP2Royalty.isZero()) t = t.add(pendingP2Royalty);
    return t;
  }

  async function handleClaim() {
    setClaiming(true);
    let claimed = false;
    try {
      // Must send transactions sequentially to avoid nonce issues
      if (pendingP2Global && !pendingP2Global.isZero()) {
        toast.info("Claiming Global shares...");
        const tx = await contracts.plan2.claimAllGlobalShares(); await tx.wait();
        claimed = true;
      }
      if (pendingP1Royalty && !pendingP1Royalty.isZero()) {
        toast.info("Claiming Star royalty...");
        const tx = await contracts.plan1.claimRoyalty(); await tx.wait();
        claimed = true;
      }
      if (pendingP2Royalty && !pendingP2Royalty.isZero()) {
        toast.info("Claiming Global royalty...");
        const tx = await contracts.plan2.claimRoyalty(); await tx.wait();
        claimed = true;
      }
      // Check claimable balance (level income, direct ref income etc go here)
      const freshBal = await contracts.data.claimableBalance(userId);
      if (freshBal.gt(0)) {
        toast.info("Claiming balance...");
        const tx = await contracts.data.claim(); await tx.wait();
        claimed = true;
      }
      if (claimed) {
        toast.success("Claim successful! Funds sent to your wallet.");
      } else {
        toast.info("Nothing to claim right now.");
      }
      await loadAll();
    } catch (err) { toast.error(err.reason || err.message || "Claim failed"); }
    finally { setClaiming(false); }
  }

  async function lookupSponsor(id, setter) {
    try {
      const rid = parseInt(id);
      if (!rid || !contracts) { setter(""); return; }
      const info = await contracts.data.getUserInfo(rid);
      setter(info.wallet === "0x0000000000000000000000000000000000000000" ? "" : (info.name || `ID: ${rid}`));
    } catch { setter(""); }
  }

  async function handleRegisterUser() {
    if (!regWallet || !regName.trim()) { toast.error("Enter wallet and name"); return; }
    setActionLoading(true);
    try {
      toast.info("Registering user...");
      const tx = await contracts.data.registerForUser(regWallet, parseInt(regRef) || userId, regName.trim());
      await tx.wait();
      toast.success("User registered!"); setRegWallet(""); setRegName(""); setRegRef(""); setRegRefName("");
    } catch (err) { toast.error(err.reason || err.message || "Registration failed"); }
    finally { setActionLoading(false); }
  }

  async function handleUpgradeUser() {
    if (!upgradeId) { toast.error("Enter user ID"); return; }
    setActionLoading(true);
    try {
      const targetId = parseInt(upgradeId); const pkg = parseInt(upgradePkg);
      const contract = upgradePlan === "1" ? contracts.plan1 : contracts.plan2;
      const prices = upgradePlan === "1" ? p1Prices : p2Prices;
      const price = prices[pkg];
      const spender = upgradePlan === "1" ? NETWORK.contracts.plan1 : NETWORK.contracts.plan2;
      const allowance = await contracts.token.allowance(account, spender);
      if (allowance.lt(price)) { toast.info("Approving..."); const a = await contracts.token.approve(spender, ethers.constants.MaxUint256); await a.wait(); }
      try { await contract.callStatic.activateForUser(targetId, pkg); }
      catch (sim) { throw new Error(sim.reason || sim.errorName || sim.message || "Simulation failed"); }
      toast.info("Activating..."); const tx = await contract.activateForUser(targetId, pkg); await tx.wait();
      toast.success("Package activated!"); setUpgradeId(""); setUpgradeRefName("");
    } catch (err) { toast.error(err.reason || err.message || "Failed"); }
    finally { setActionLoading(false); }
  }

  function handleCopyRef() { copyToClipboard(`${window.location.origin}?ref=${userId}`); toast.success("Referral link copied!"); }

  async function loadInactiveMembers() {
    if (inactiveMembers.length > 0) { setShowInactive(!showInactive); return; }
    setInactiveLoading(true);
    setShowInactive(true);
    try {
      // Fetch all direct refs across pages
      const PAGE_SIZE = 100;
      let allRefs = [];
      let page = 0;
      let total = 0;
      while (true) {
        const res = await contracts.data.getDirectRefDetails(userId, page, PAGE_SIZE);
        total = res.total.toNumber();
        for (let i = 0; i < res.ids.length; i++) {
          const uid = res.ids[i].toNumber();
          if (uid === 0) continue;
          if (!res.active_[i]) {
            allRefs.push({ id: uid, wallet: res.wallets[i] });
          }
        }
        page++;
        if (page * PAGE_SIZE >= total) break;
        if (page > 50) break; // safety cap
      }

      // Sort newest first (highest ID = most recent registration)
      allRefs.sort((a, b) => b.id - a.id);

      // Fetch names in batches of 20
      const inactive = [];
      for (let i = 0; i < allRefs.length; i += 20) {
        const batch = allRefs.slice(i, i + 20);
        const names = await Promise.all(batch.map(async (r) => {
          try {
            const info = await contracts.data.getUserInfo(r.id);
            return info.name || "";
          } catch { return ""; }
        }));
        for (let j = 0; j < batch.length; j++) {
          inactive.push({ ...batch[j], name: names[j] });
        }
      }

      setInactiveMembers(inactive);
      setInactivePage(0);
    } catch (err) { console.error("loadInactive:", err); }
    finally { setInactiveLoading(false); }
  }

  async function loadDirectMembers() {
    if (directMembers.length > 0) { setShowDirect(!showDirect); return; }
    setDirectLoading(true);
    setShowDirect(true);
    try {
      const PAGE_SIZE = 100;
      let allRefs = [];
      let page = 0;
      let total = 0;
      while (true) {
        const res = await contracts.data.getDirectRefDetails(userId, page, PAGE_SIZE);
        total = res.total.toNumber();
        for (let i = 0; i < res.ids.length; i++) {
          const uid = res.ids[i].toNumber();
          if (uid === 0) continue;
          allRefs.push({ id: uid, wallet: res.wallets[i], active: res.active_[i] });
        }
        page++;
        if (page * PAGE_SIZE >= total) break;
        if (page > 50) break;
      }

      // Newest first
      allRefs.sort((a, b) => b.id - a.id);

      // Fetch name + packages in batches of 15
      const members = [];
      for (let i = 0; i < allRefs.length; i += 15) {
        const batch = allRefs.slice(i, i + 15);
        const details = await Promise.all(batch.map(async (r) => {
          try {
            const [info, p1, p2] = await Promise.all([
              contracts.data.getUserInfo(r.id),
              contracts.plan1.getAllPackages(r.id),
              contracts.plan2.getAllPackages(r.id),
            ]);
            const starPkgs = [];
            const globalPkgs = [];
            const prices = ["$1","$2","$4","$8","$16","$32","$64","$128"];
            for (let p = 0; p < 8; p++) {
              if (p1.a[p]) starPkgs.push(prices[p]);
              if (p2.a[p]) globalPkgs.push(prices[p]);
            }
            return { ...r, name: info.name || "", starPkgs, globalPkgs };
          } catch { return { ...r, name: "", starPkgs: [], globalPkgs: [] }; }
        }));
        members.push(...details);
      }

      setDirectMembers(members);
      setDirectPage(0);
    } catch (err) { console.error("loadDirect:", err); }
    finally { setDirectLoading(false); }
  }

  if (!account) return <Navigate to="/" replace />;
  if (loading && !userInfo) return <Loader message="Loading..." />;
  if (userId === 0) return <Navigate to={ref ? `/register?ref=${ref}` : "/register"} replace />;

  const pkgLabels = upgradePlan === "1" ? PLAN1_PKG_LABELS : PLAN2_PKG_LABELS;
  const pkgCount = upgradePlan === "1" ? PLAN1_PKG_COUNT : PLAN2_PKG_COUNT;
  const pkgPrices = upgradePlan === "1" ? p1Prices : p2Prices;

  return (
    <div className="home-tab">
      {/* Profile */}
      <div className="ht-profile">
        <div className="ht-profile-top">
          <div className="ht-avatar"><span className="ht-avatar-icon">&#9730;</span></div>
          <div className="ht-profile-info">
            <div className="ht-profile-name">{userInfo?.name || "User"}</div>
            <div className="ht-profile-uid">ID: <span className="ht-uid-highlight">{userId}</span></div>
            <div className="ht-profile-wallet">{truncateAddress(account)}</div>
          </div>
          <div className="ht-profile-status"><span className="ht-status-dot" /><span>Active</span></div>
        </div>
        {joinDate && (
          <div className="ht-doj">Date of Joining: {new Date(joinDate.toNumber() * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</div>
        )}
        <div className="ht-profile-meta">
          <div className="ht-meta-item"><span className="ht-meta-label">Sponsor</span><span className="ht-meta-value">{userInfo?.referrerId || "-"}</span></div>
          <div className="ht-meta-item"><span className="ht-meta-label">Direct Refs</span><span className="ht-meta-value">{userInfo?.directRefCount || 0}</span></div>
          <div className="ht-meta-item"><span className="ht-meta-label">Star Team</span><span className="ht-meta-value">{starTeamCount}</span></div>
          <div className="ht-meta-item"><span className="ht-meta-label">Global Team</span><span className="ht-meta-value">{globalTeamCount}</span></div>
        </div>
        <div className="ht-ref-row">
          <input className="ht-ref-input" value={`${window.location.origin}?ref=${userId}`} readOnly onClick={handleCopyRef} />
          <button className="ht-ref-btn" onClick={handleCopyRef}>Copy Link</button>
        </div>
      </div>

      {/* Current Packages */}
      <div className="ht-current-pkgs">
        <div className="ht-cpkg">
          <span className="ht-cpkg-plan">Star</span>
          <span className="ht-cpkg-value">{starHighestPkg >= 0 ? PLAN1_PKG_LABELS[starHighestPkg] : "None"}</span>
        </div>
        <div className="ht-cpkg ht-cpkg-global">
          <span className="ht-cpkg-plan">Global</span>
          <span className="ht-cpkg-value">{globalHighestPkg >= 0 ? PLAN2_PKG_LABELS[globalHighestPkg] : "None"}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="ht-stats">
        <div className="ht-stat-item"><span className="ht-stat-label">Invested</span><span className="ht-stat-val">{formatToken(activeInvested, 18, 0)}</span><span className="ht-stat-unit">{TOKEN_SYMBOL}</span></div>
        <div className="ht-stat-item"><span className="ht-stat-label">Star Earned</span><span className="ht-stat-val ht-stat-cyan">{p1Income ? formatToken(p1Income.direct.add(p1Income.level).add(p1Income.royalty)) : "0.0000"}</span><span className="ht-stat-unit">{TOKEN_SYMBOL}</span></div>
        <div className="ht-stat-item"><span className="ht-stat-label">Global Earned</span><span className="ht-stat-val ht-stat-purple">{p2Income ? formatToken(p2Income.direct.add(p2Income.level).add(p2Income.global).add(p2Income.royalty)) : "0.0000"}</span><span className="ht-stat-unit">{TOKEN_SYMBOL}</span></div>
      </div>

      {/* Total Earned — Premium Green Compact Badge */}
      <div className="ht-inc-badge ht-inc-earned">
        <div className="ht-inc-glow" />
        <div className="ht-inc-icon">&#9733;</div>
        <div className="ht-inc-body">
          <span className="ht-inc-label">Total Earned</span>
          <div className="ht-inc-amount-row">
            <span className="ht-inc-amount">{formatToken(userInfo?.totalEarned)}</span>
            <span className="ht-inc-unit">{TOKEN_SYMBOL}</span>
          </div>
        </div>
        <div className="ht-inc-side">Lifetime</div>
      </div>

      {/* Auto Upgrade Hold — Small Purple Compact Chip */}
      {autoUpgradeHold && !autoUpgradeHold.isZero() && (
        <div className="ht-inc-chip ht-inc-hold">
          <div className="ht-chip-icon">&#8635;</div>
          <div className="ht-chip-body">
            <span className="ht-chip-label">Auto Upgrade Hold</span>
            <span className="ht-chip-sub">Reserved for next package</span>
          </div>
          <div className="ht-chip-amt">
            <span className="ht-chip-val">{formatToken(autoUpgradeHold)}</span>
            <span className="ht-chip-unit">{TOKEN_SYMBOL}</span>
          </div>
        </div>
      )}

      {/* Royalty Achiever Badges */}
      {(p1RoyaltyAchiever || p2RoyaltyAchiever) && (
        <div className="ht-achievers">
          {p1RoyaltyAchiever && (
            <div className="ht-achiever ht-achiever-star">
              <div className="ht-achiever-glow" />
              <div className="ht-achiever-icon">&#9819;</div>
              <div className="ht-achiever-body">
                <span className="ht-achiever-tier">STAR ROYALTY</span>
                <span className="ht-achiever-title">Achiever</span>
              </div>
              <div className="ht-achiever-stars">
                <span>&#9733;</span><span>&#9733;</span><span>&#9733;</span>
              </div>
            </div>
          )}
          {p2RoyaltyAchiever && (
            <div className="ht-achiever ht-achiever-global">
              <div className="ht-achiever-glow" />
              <div className="ht-achiever-icon">&#9819;</div>
              <div className="ht-achiever-body">
                <span className="ht-achiever-tier">GLOBAL ROYALTY</span>
                <span className="ht-achiever-title">Achiever</span>
              </div>
              <div className="ht-achiever-stars">
                <span>&#9733;</span><span>&#9733;</span><span>&#9733;</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Claim */}
      <div className="ht-claim-block">
        <ClaimSection claimable={getTotalAvailable()} onClaim={handleClaim} processing={claiming} />
        <div className="ht-pending-grid">
          <div className="ht-pending-item"><span className="ht-pending-label">Global Share</span><span className="ht-pending-val">{pendingP2Global ? formatToken(pendingP2Global) : "0.0000"}</span></div>
          <div className="ht-pending-item"><span className="ht-pending-label">Star Royalty</span><span className="ht-pending-val">{pendingP1Royalty ? formatToken(pendingP1Royalty) : "0.0000"}</span></div>
          <div className="ht-pending-item"><span className="ht-pending-label">Global Royalty</span><span className="ht-pending-val">{pendingP2Royalty ? formatToken(pendingP2Royalty) : "0.0000"}</span></div>
        </div>
      </div>

      {/* Income Breakdown */}
      <div className="ht-income-section">
        <h3 className="ht-section-title">Income Breakdown</h3>
        <div className="ht-income-grid">
          <div className="ht-income-card">
            <h4 className="ht-income-plan">Star Plan</h4>
            <div className="ht-inc-row"><span>Direct Ref</span><span>{p1Income ? formatToken(p1Income.direct) : "0.0000"}</span></div>
            <div className="ht-inc-row"><span>Level Income</span><span>{p1Income ? formatToken(p1Income.level) : "0.0000"}</span></div>
            <div className="ht-inc-row"><span>Royalty</span><span>{p1Income ? formatToken(p1Income.royalty) : "0.0000"}</span></div>
          </div>
          <div className="ht-income-card">
            <h4 className="ht-income-plan ht-income-plan-global">Global Plan</h4>
            <div className="ht-inc-row"><span>Direct Ref</span><span>{p2Income ? formatToken(p2Income.direct) : "0.0000"}</span></div>
            <div className="ht-inc-row"><span>Level</span><span>{p2Income ? formatToken(p2Income.level) : "0.0000"}</span></div>
            <div className="ht-inc-row"><span>Global Share</span><span>{p2Income ? formatToken(p2Income.global) : "0.0000"}</span></div>
            <div className="ht-inc-row"><span>Royalty</span><span>{p2Income ? formatToken(p2Income.royalty) : "0.0000"}</span></div>
          </div>
        </div>
      </div>

      {/* Register & Upgrade */}
      <div className="ht-action-section">
        <h3 className="ht-section-title">Register & Upgrade User</h3>
        <div className="ht-action-grid">
          <div className="ht-action-card">
            <h4 className="ht-action-card-title">Register New User</h4>
            <div className="form-group"><input className="form-input" placeholder="Wallet address (0x...)" value={regWallet} onChange={(e) => setRegWallet(e.target.value)} /></div>
            <div className="form-group"><input className="form-input" placeholder="Name" value={regName} onChange={(e) => setRegName(e.target.value)} /></div>
            <div className="form-group">
              <input className="form-input" placeholder={`Sponsor ID (default: ${userId})`} value={regRef}
                onChange={(e) => { setRegRef(e.target.value); lookupSponsor(e.target.value, setRegRefName); }} />
              {regRefName && <span className="form-ref-name">Sponsor: {regRefName}</span>}
            </div>
            <button className="btn-neon" onClick={handleRegisterUser} disabled={actionLoading}>{actionLoading ? "Processing..." : "Register"}</button>
          </div>
          <div className="ht-action-card">
            <h4 className="ht-action-card-title">Upgrade User Package</h4>
            <div className="form-group">
              <input className="form-input" placeholder="User ID" value={upgradeId}
                onChange={(e) => { setUpgradeId(e.target.value); lookupSponsor(e.target.value, setUpgradeRefName); }} />
              {upgradeRefName && <span className="form-ref-name">{upgradeRefName}</span>}
            </div>
            <div className="form-group">
              <select className="form-input" value={upgradePlan} onChange={(e) => { setUpgradePlan(e.target.value); setUpgradePkg("0"); }}>
                <option value="1">Star Plan</option><option value="2">Global Plan</option>
              </select>
            </div>
            <div className="form-group">
              <select className="form-input" value={upgradePkg} onChange={(e) => setUpgradePkg(e.target.value)}>
                {Array.from({ length: pkgCount }, (_, i) => (
                  <option key={i} value={i}>{pkgLabels[i]} - {pkgPrices[i] ? formatToken(pkgPrices[i], 18, 0) : "..."} {TOKEN_SYMBOL}</option>
                ))}
              </select>
            </div>
            <button className="btn-neon btn-secondary" onClick={handleUpgradeUser} disabled={actionLoading}>{actionLoading ? "Processing..." : "Upgrade"}</button>
          </div>
        </div>
      </div>

      {/* Non-Active Members */}
      <div className="ht-inactive-section">
        <button className="ht-inactive-btn" onClick={loadInactiveMembers} disabled={inactiveLoading}>
          {inactiveLoading ? "Loading..." : showInactive ? "Hide Non-Active Direct Members" : "Show Non-Active Direct Members"}
        </button>
        {showInactive && (() => {
          const PAGE_SIZE = 50;
          const totalPages = Math.ceil(inactiveMembers.length / PAGE_SIZE);
          const pageItems = inactiveMembers.slice(inactivePage * PAGE_SIZE, (inactivePage + 1) * PAGE_SIZE);
          return (
            <div className="ht-inactive-list">
              {inactiveMembers.length === 0 && !inactiveLoading ? (
                <div className="ht-inactive-empty">All direct referrals are active!</div>
              ) : (
                <>
                  <div className="ht-inactive-header">
                    Showing {inactivePage * PAGE_SIZE + 1}-{Math.min((inactivePage + 1) * PAGE_SIZE, inactiveMembers.length)} of {inactiveMembers.length}
                  </div>
                  {pageItems.map((u) => (
                    <div key={u.id} className="ht-inactive-item">
                      <span className="ht-inactive-name">{u.name || "User"}</span>
                      <span className="ht-inactive-id">#{u.id}</span>
                      <span className="ht-inactive-wallet">{truncateAddress(u.wallet)}</span>
                    </div>
                  ))}
                  {totalPages > 1 && (
                    <div className="ht-inactive-pagination">
                      <button className="page-btn" disabled={inactivePage === 0} onClick={() => setInactivePage(inactivePage - 1)}>Prev</button>
                      <span className="page-info">Page {inactivePage + 1} of {totalPages}</span>
                      <button className="page-btn" disabled={inactivePage >= totalPages - 1} onClick={() => setInactivePage(inactivePage + 1)}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Direct Members List */}
      <div className="ht-direct-section">
        <button className="ht-direct-btn" onClick={loadDirectMembers} disabled={directLoading}>
          {directLoading ? "Loading..." : showDirect ? "Hide Direct Members" : "View Direct Members List"}
        </button>
        {showDirect && (() => {
          const PAGE_SIZE = 50;
          const totalPages = Math.ceil(directMembers.length / PAGE_SIZE);
          const pageItems = directMembers.slice(directPage * PAGE_SIZE, (directPage + 1) * PAGE_SIZE);
          return (
            <div className="ht-direct-list">
              {directMembers.length === 0 && !directLoading ? (
                <div className="ht-direct-empty">No direct members yet</div>
              ) : (
                <>
                  <div className="ht-direct-header">
                    Showing {directPage * PAGE_SIZE + 1}-{Math.min((directPage + 1) * PAGE_SIZE, directMembers.length)} of {directMembers.length}
                  </div>
                  {pageItems.map((u) => (
                    <div key={u.id} className="ht-direct-card">
                      <div className="ht-direct-top">
                        <span className="ht-direct-name">{u.name || "User"}</span>
                        <span className="ht-direct-id">#{u.id}</span>
                        <span className={`ht-direct-status ${u.active ? "text-green" : "text-red"}`}>{u.active ? "Active" : "Inactive"}</span>
                      </div>
                      {(u.starPkgs.length > 0 || u.globalPkgs.length > 0) && (
                        <div className="ht-direct-pkgs">
                          {u.starPkgs.length > 0 && (
                            <div className="ht-direct-pkg-row">
                              <span className="ht-direct-pkg-label">Star:</span>
                              {u.starPkgs.map((p, i) => <span key={i} className="ht-direct-pkg-badge ht-direct-pkg-star">{p}</span>)}
                            </div>
                          )}
                          {u.globalPkgs.length > 0 && (
                            <div className="ht-direct-pkg-row">
                              <span className="ht-direct-pkg-label">Global:</span>
                              {u.globalPkgs.map((p, i) => <span key={i} className="ht-direct-pkg-badge ht-direct-pkg-global">{p}</span>)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {totalPages > 1 && (
                    <div className="ht-direct-pagination">
                      <button className="page-btn" disabled={directPage === 0} onClick={() => setDirectPage(directPage - 1)}>Prev</button>
                      <span className="page-info">Page {directPage + 1} of {totalPages}</span>
                      <button className="page-btn" disabled={directPage >= totalPages - 1} onClick={() => setDirectPage(directPage + 1)}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </div>

      <button className="ht-refresh" onClick={loadAll} disabled={loading}>{loading ? "Refreshing..." : "Refresh Data"}</button>
    </div>
  );
}
